import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastService } from '../../../../../../../../@core/services/toast.service';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { ScoringService } from '../../../../../../../../@shared/services/scoring.service';
import {
  EventResponse,
  EventClassResponse,
  ClassRoundResponse,
  ClassResultResponse,
  ClassScoringConfigResponse,
  FinalPositionResponse,
  FinalResultTypeName
} from '../../../../../../../../@shared/models/response.module';
import { ResultEntryModal } from '../manage-event-classes/components/result-entry-modal/result-entry-modal';
import { firstValueFrom } from 'rxjs';

interface AggregatedResult {
  class_registration_id: number;
  user_name: string;
  horse_name: string;
  rounds: { [round_id: number]: ClassResultResponse | null };
  total_time: number | null;
  total_points: number | null;
  best_time: number | null;
  best_points: number | null;
  final_position?: number;
}

@Component({
  selector: 'app-manage-event-results',
  standalone: false,
  templateUrl: './manage-event-results.html',
  styleUrl: './manage-event-results.css'
})
export class ManageEventResults implements OnInit {
  @Input() event: EventResponse;
  @Input() initialClassId: number | null = null;

  // Data
  eventClasses: EventClassResponse[] = [];
  selectedClass: EventClassResponse | null = null;
  scoringConfig: ClassScoringConfigResponse | null = null;
  rounds: ClassRoundResponse[] = [];
  selectedRound: ClassRoundResponse | null = null;
  results: ClassResultResponse[] = [];
  resultsMap: Map<number, ClassResultResponse> = new Map();

  // Aggregated results for final standings
  aggregatedResults: AggregatedResult[] = [];
  manualPositions: FinalPositionResponse[] = [];

  // View mode
  viewMode: 'round' | 'final' = 'round';

  // Final result type options
  finalResultTypeOptions: { value: FinalResultTypeName; label: string }[] = [];

  loading = false;
  saving = false;

  constructor(
    private eventsService: EventsService,
    private scoringService: ScoringService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.initFinalResultTypeOptions();
    this.loadClasses();
  }

  initFinalResultTypeOptions() {
    this.finalResultTypeOptions = [
      { value: 'combined_total', label: this.translateService.instant('results.combinedTotal') },
      { value: 'best_round', label: this.translateService.instant('results.bestRound') },
      { value: 'manual', label: this.translateService.instant('results.manual') }
    ];
  }

  async loadClasses() {
    this.loading = true;
    try {
      this.eventClasses = await firstValueFrom(
        this.eventsService.getEventClasses({ event_id: this.event.event_id })
      ) || [];

      // Auto-select class based on initialClassId or first class
      if (this.eventClasses.length > 0) {
        let classToSelect = this.eventClasses[0];

        // If initialClassId is provided, find and select that class
        if (this.initialClassId) {
          const targetClass = this.eventClasses.find(c => c.event_class_id === this.initialClassId);
          if (targetClass) {
            classToSelect = targetClass;
          }
        }

        await this.selectClass(classToSelect);
      }
    } catch (e) {
      this.eventClasses = [];
    } finally {
      this.loading = false;
    }
  }

  async selectClass(eventClass: EventClassResponse) {
    this.selectedClass = eventClass;
    this.selectedRound = null;
    this.results = [];
    this.resultsMap.clear();

    // Load scoring config
    try {
      this.scoringConfig = await firstValueFrom(
        this.scoringService.getScoringConfig({ event_class_id: eventClass.event_class_id })
      );
    } catch (e) {
      this.scoringConfig = null;
    }

    // Load rounds
    try {
      this.rounds = await firstValueFrom(
        this.scoringService.getRounds({ event_class_id: eventClass.event_class_id })
      ) || [];

      if (this.rounds.length > 0) {
        await this.selectRound(this.rounds[0]);
      }
    } catch (e) {
      this.rounds = [];
    }

    // Load manual positions if in manual mode
    await this.loadManualPositions();

    // Load aggregated results for final standings
    await this.loadAggregatedResults();
  }

  async loadManualPositions() {
    if (!this.selectedClass) return;

    try {
      this.manualPositions = await firstValueFrom(
        this.scoringService.getFinalPositions({ event_class_id: this.selectedClass.event_class_id })
      ) || [];
    } catch (e) {
      this.manualPositions = [];
    }
  }

  async selectRound(round: ClassRoundResponse) {
    this.selectedRound = round;
    this.viewMode = 'round';
    await this.loadResults();
  }

  async loadResults() {
    if (!this.selectedClass || !this.selectedRound) return;

    try {
      this.results = await firstValueFrom(
        this.scoringService.getResults({
          event_class_id: this.selectedClass.event_class_id,
          round_id: this.selectedRound.round_id
        })
      ) || [];

      this.resultsMap.clear();
      for (const result of this.results) {
        this.resultsMap.set(result.class_registration_id, result);
      }
    } catch (e) {
      this.results = [];
      this.resultsMap.clear();
    }
  }

  async loadAggregatedResults() {
    if (!this.selectedClass) return;

    const registrations = this.selectedClass.event_class_registrations || [];
    const aggregated: AggregatedResult[] = [];

    // Load results for all rounds
    const allResults: ClassResultResponse[] = [];
    for (const round of this.rounds) {
      try {
        const roundResults = await firstValueFrom(
          this.scoringService.getResults({
            event_class_id: this.selectedClass.event_class_id,
            round_id: round.round_id
          })
        ) || [];
        allResults.push(...roundResults);
      } catch (e) {
        // Continue with other rounds
      }
    }

    // Build aggregated results
    for (const reg of registrations) {
      const aggResult: AggregatedResult = {
        class_registration_id: reg.class_registration_id,
        user_name: reg.user ? `${reg.user.first_name || ''} ${reg.user.last_name || ''}`.trim() : 'Unknown',
        horse_name: reg.horse?.name || '',
        rounds: {},
        total_time: null,
        total_points: null,
        best_time: null,
        best_points: null
      };

      let totalTime = 0;
      let totalPoints = 0;
      let hasTime = false;
      let hasPoints = false;
      let bestTime: number | null = null;
      let bestPoints: number | null = null;

      for (const round of this.rounds) {
        const result = allResults.find(
          r => r.class_registration_id === reg.class_registration_id && r.round_id === round.round_id
        );
        aggResult.rounds[round.round_id] = result || null;

        if (result && result.status === 'recorded') {
          if (result.final_time_seconds !== null && result.final_time_seconds !== undefined) {
            totalTime += result.final_time_seconds;
            hasTime = true;
            // Track best time (lowest)
            if (bestTime === null || result.final_time_seconds < bestTime) {
              bestTime = result.final_time_seconds;
            }
          }
          if (result.final_points !== null && result.final_points !== undefined) {
            totalPoints += result.final_points;
            hasPoints = true;
            // Track best points (highest for points_high, lowest for points_low)
            const scoreType = this.scoringConfig?.score_type || 'timed';
            if (scoreType === 'points_high') {
              if (bestPoints === null || result.final_points > bestPoints) {
                bestPoints = result.final_points;
              }
            } else {
              if (bestPoints === null || result.final_points < bestPoints) {
                bestPoints = result.final_points;
              }
            }
          }
        }
      }

      aggResult.total_time = hasTime ? totalTime : null;
      aggResult.total_points = hasPoints ? totalPoints : null;
      aggResult.best_time = bestTime;
      aggResult.best_points = bestPoints;

      aggregated.push(aggResult);
    }

    // Sort based on final result type
    this.sortAggregatedResults(aggregated);

    // Assign positions
    aggregated.forEach((r, index) => {
      r.final_position = index + 1;
    });

    this.aggregatedResults = aggregated;
  }

  sortAggregatedResults(results: AggregatedResult[]) {
    const scoreType = this.scoringConfig?.score_type || 'timed';
    const finalResultType = this.scoringConfig?.final_result_type || 'combined_total';

    // If manual, sort by saved positions
    if (finalResultType === 'manual' && this.manualPositions.length > 0) {
      const positionMap = new Map<number, number>();
      this.manualPositions.forEach(p => positionMap.set(p.class_registration_id, p.position));

      results.sort((a, b) => {
        const posA = positionMap.get(a.class_registration_id) ?? 9999;
        const posB = positionMap.get(b.class_registration_id) ?? 9999;
        return posA - posB;
      });
      return;
    }

    results.sort((a, b) => {
      // Determine which value to use based on final result type
      let aValue: number | null;
      let bValue: number | null;

      if (finalResultType === 'best_round') {
        aValue = scoreType === 'timed' ? a.best_time : a.best_points;
        bValue = scoreType === 'timed' ? b.best_time : b.best_points;
      } else {
        // combined_total
        aValue = scoreType === 'timed' ? a.total_time : a.total_points;
        bValue = scoreType === 'timed' ? b.total_time : b.total_points;
      }

      // DNF/DNS/DQ go to bottom
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (scoreType === 'timed') {
        return aValue - bValue;
      } else if (scoreType === 'points_high') {
        return bValue - aValue;
      } else if (scoreType === 'points_low') {
        return aValue - bValue;
      }
      return 0;
    });
  }

  showFinalStandings() {
    this.viewMode = 'final';
    this.selectedRound = null;
  }

  getResultForRegistration(registration: any): ClassResultResponse | undefined {
    return this.resultsMap.get(registration.class_registration_id);
  }

  openResultEntryModal(registration: any, existingResult?: ClassResultResponse) {
    if (!this.selectedClass || !this.selectedRound) {
      this.toastService.error(this.translateService.instant('results.noRoundSelected'));
      return;
    }

    const modalRef = this.modalService.open(ResultEntryModal, { size: 'lg', centered: true });
    modalRef.componentInstance.eventClass = this.selectedClass;
    modalRef.componentInstance.registration = registration;
    modalRef.componentInstance.round = this.selectedRound;
    modalRef.componentInstance.existingResult = existingResult;

    modalRef.result.then(async (saved) => {
      if (saved) {
        await this.loadResults();
        await this.loadAggregatedResults();
      }
    }, () => {});
  }

  // Drag and drop for manual ordering
  onDrop(event: CdkDragDrop<AggregatedResult[]>) {
    moveItemInArray(this.aggregatedResults, event.previousIndex, event.currentIndex);
    // Update positions after drag
    this.aggregatedResults.forEach((r, index) => {
      r.final_position = index + 1;
    });
  }

  async saveFinalPositions() {
    if (!this.selectedClass) return;

    this.saving = true;
    try {
      const positions = this.aggregatedResults.map((r, index) => ({
        class_registration_id: r.class_registration_id,
        position: index + 1
      }));

      await firstValueFrom(
        this.scoringService.saveFinalPositions({
          event_class_id: this.selectedClass.event_class_id,
          positions
        })
      );

      this.toastService.success(this.translateService.instant('results.positionsSaved'));
      await this.loadManualPositions();
    } catch (e) {
      this.toastService.error(this.translateService.instant('results.positionsSaveError'));
    } finally {
      this.saving = false;
    }
  }

  async updateFinalResultType(type: FinalResultTypeName) {
    if (!this.selectedClass || !this.scoringConfig) return;

    this.saving = true;
    try {
      await firstValueFrom(
        this.scoringService.saveScoringConfig({
          event_class_id: this.selectedClass.event_class_id,
          score_type: this.scoringConfig.score_type,
          penalty_type: this.scoringConfig.penalty_type,
          time_weight: this.scoringConfig.time_weight,
          points_weight: this.scoringConfig.points_weight,
          decimal_places: this.scoringConfig.decimal_places,
          final_result_type: type
        })
      );

      this.scoringConfig.final_result_type = type;
      await this.loadAggregatedResults();
      this.toastService.success(this.translateService.instant('results.configSaved'));
    } catch (e) {
      this.toastService.error(this.translateService.instant('results.configSaveError'));
    } finally {
      this.saving = false;
    }
  }

  formatTime(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined) return '-';
    return seconds.toFixed(3) + 's';
  }

  formatPoints(points: number | null | undefined): string {
    if (points === null || points === undefined) return '-';
    return points.toFixed(2);
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'recorded': return 'badge-light-success';
      case 'dnf': return 'badge-light-warning';
      case 'dns': return 'badge-light-secondary';
      case 'dq': return 'badge-light-danger';
      default: return 'badge-light-info';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'recorded': return 'Recorded';
      case 'dnf': return 'DNF';
      case 'dns': return 'DNS';
      case 'dq': return 'DQ';
      default: return status;
    }
  }

  get isTimeBased(): boolean {
    return this.scoringConfig?.score_type === 'timed';
  }

  get isPointsBased(): boolean {
    return this.scoringConfig?.score_type === 'points_high' || this.scoringConfig?.score_type === 'points_low';
  }

  get isManualMode(): boolean {
    return this.scoringConfig?.final_result_type === 'manual';
  }

  get isBestRoundMode(): boolean {
    return this.scoringConfig?.final_result_type === 'best_round';
  }

  get finalResultType(): FinalResultTypeName {
    return this.scoringConfig?.final_result_type || 'combined_total';
  }

  getFinalResultLabel(): string {
    const type = this.finalResultType;
    switch (type) {
      case 'combined_total': return this.translateService.instant('results.combinedTotal');
      case 'best_round': return this.translateService.instant('results.bestRound');
      case 'manual': return this.translateService.instant('results.manual');
      default: return '';
    }
  }

  getInitials(firstName?: string | null, lastName?: string | null): string {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || '?';
  }

  getInitialsFromFullName(fullName?: string | null): string {
    if (!fullName) return '?';
    const parts = fullName.split(' ');
    const first = parts[0]?.charAt(0)?.toUpperCase() || '';
    const last = parts[1]?.charAt(0)?.toUpperCase() || '';
    return first + last || '?';
  }

  getRoundResultsCount(round: ClassRoundResponse): number {
    if (!this.selectedClass) return 0;
    // Count results that exist for this round from aggregated data
    return this.aggregatedResults.filter(agg =>
      agg.rounds[round.round_id] && agg.rounds[round.round_id]?.status === 'recorded'
    ).length;
  }

  isRoundComplete(round: ClassRoundResponse): boolean {
    if (!this.selectedClass) return false;
    const totalParticipants = this.selectedClass.event_class_registrations?.length || 0;
    const resultsCount = this.getRoundResultsCount(round);
    return totalParticipants > 0 && resultsCount === totalParticipants;
  }

  isRoundStarted(round: ClassRoundResponse): boolean {
    return this.getRoundResultsCount(round) > 0;
  }

  getPositionInRound(registration: any): number {
    if (!this.selectedRound) return 0;

    // Get all results for this round sorted by time/points
    const roundResults = Array.from(this.resultsMap.values())
      .filter(r => r.status === 'recorded')
      .sort((a, b) => {
        if (this.isTimeBased) {
          return (a.final_time_seconds || 0) - (b.final_time_seconds || 0);
        } else if (this.scoringConfig?.score_type === 'points_high') {
          return (b.final_points || 0) - (a.final_points || 0);
        } else {
          return (a.final_points || 0) - (b.final_points || 0);
        }
      });

    const position = roundResults.findIndex(r => r.class_registration_id === registration.class_registration_id);
    return position >= 0 ? position + 1 : 0;
  }

  isBestRound(agg: AggregatedResult, round: ClassRoundResponse): boolean {
    if (!this.isBestRoundMode) return false;

    const roundResult = agg.rounds[round.round_id];
    if (!roundResult || roundResult.status !== 'recorded') return false;

    if (this.isTimeBased) {
      return roundResult.final_time_seconds === agg.best_time;
    } else {
      return roundResult.final_points === agg.best_points;
    }
  }
}
